"use client";

/**
 * BookingAuthGate — auth gate inline placée à la fin du flow de réservation.
 *
 * Pattern Doctolib : titre rappelant l'engagement RDV + timer 15min, puis
 * le AuthFlowClient (Choice → Email/Password ou Signup) avec callback de
 * succès qui ferme le gate et continue vers la confirmation.
 */
import { useState } from "react";
import { useTranslations } from "next-intl";
import { Link } from "@/lib/i18n/navigation";
import { Calendar, ChatCircle, Heart, ArrowLeft, Eye, EyeSlash, Clock } from "@phosphor-icons/react/dist/ssr";

type Step = "choice" | "email" | "password" | "signup" | "totp";

type Props = {
  minutesLeft: number;
  onAuthSuccess: () => void | Promise<void>;
};

export function BookingAuthGate({ minutesLeft, onAuthSuccess }: Props) {
  const t = useTranslations("booking.authGate");
  const [step, setStep] = useState<Step>("choice");
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [challengeToken, setChallengeToken] = useState<string | null>(null);
  const [totpCode, setTotpCode] = useState("");

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [confirm, setConfirm] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);

  function back() {
    setError("");
    if (step === "email") setStep("choice");
    else if (step === "password") setStep("email");
    else if (step === "signup") setStep("choice");
    else if (step === "totp") setStep("password");
  }

  async function submitEmail(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    if (!identifier.trim()) return;
    setStep("password");
  }

  async function submitPassword(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "content-type": "application/json" },
        // Booking gate = parcours patient mais médecin peut aussi prendre
        // RDV pour lui-même comme patient. Refuse seulement ADMIN.
        body: JSON.stringify({ email: identifier.trim().toLowerCase(), password, allowedRoles: ["PATIENT", "PHYSICIAN"] }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || t("errors.loginFailed"));
        setLoading(false);
        return;
      }
      if (data.mfaRequired) {
        setChallengeToken(data.challengeToken);
        setStep("totp");
        setLoading(false);
        return;
      }
      await onAuthSuccess();
    } catch {
      setError(t("errors.serverDown"));
      setLoading(false);
    }
  }

  async function submitTotp(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch("/api/auth/2fa/verify", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ token: totpCode, challengeToken }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || t("errors.totpInvalid"));
        setLoading(false);
        return;
      }
      await onAuthSuccess();
    } catch {
      setError(t("errors.serverDown"));
      setLoading(false);
    }
  }

  async function submitSignup(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    if (password !== confirm) { setError(t("errors.passwordMismatch")); return; }
    if (password.length < 8) { setError(t("errors.passwordTooShort")); return; }
    if (!acceptTerms) { setError(t("errors.termsRequired")); return; }
    setLoading(true);
    try {
      const res = await fetch("/api/auth/register", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          email: identifier.trim().toLowerCase(),
          password,
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          phone: phone.trim(),
          role: "patient",
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || t("errors.signupFailed"));
        setLoading(false);
        return;
      }
      await onAuthSuccess();
    } catch {
      setError(t("errors.serverDown"));
      setLoading(false);
    }
  }

  const cardCommon = "rounded-2xl border border-[var(--sahha-border-subtle)] bg-white p-6 shadow-[0_2px_24px_-12px_rgba(15,23,42,0.10)]";

  // Voluntarily ignored linter for setRemember used to keep checkbox interactive.
  void remember; void setRemember;

  return (
    <div className="space-y-5">
      {/* Header rappel + timer */}
      <div className="space-y-2">
        <h2 className="text-xl font-bold text-[var(--sahha-text-900)] sm:text-2xl">
          {t("headerTitle")}
        </h2>
        <div className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1.5 text-[13px] font-semibold text-amber-800 ring-1 ring-amber-200">
          <Clock size={14} weight="fill" />
          {t("timer", { minutes: minutesLeft })}
        </div>
      </div>

      {/* CHOICE */}
      {step === "choice" && (
        <div className="space-y-4">
          <div className={cardCommon}>
            <h3 className="text-[18px] font-bold text-[var(--sahha-text-900)]">{t("newToSahha")}</h3>
            <ul className="mt-4 space-y-2.5">
              <Bullet icon={<Calendar size={16} weight="duotone" />} label={t("benefitReminders")} />
              <Bullet icon={<ChatCircle size={16} weight="duotone" />} label={t("benefitChat")} />
              <Bullet icon={<Heart size={16} weight="duotone" />} label={t("benefitRecord")} />
            </ul>
            <button
              type="button"
              onClick={() => setStep("signup")}
              className="mt-5 inline-flex h-12 w-full items-center justify-center rounded-full bg-[var(--sahha-primary-600)] text-[13px] font-bold uppercase tracking-wider text-white hover:bg-[var(--sahha-primary-700)]"
            >
              {t("createAccount")}
            </button>
          </div>
          <div className={cardCommon}>
            <h3 className="text-[18px] font-bold text-[var(--sahha-text-900)]">{t("alreadyAccount")}</h3>
            <button
              type="button"
              onClick={() => setStep("email")}
              className="mt-4 inline-flex h-12 w-full items-center justify-center rounded-full border-2 border-[var(--sahha-primary-600)] bg-white text-[13px] font-bold uppercase tracking-wider text-[var(--sahha-primary-700)] hover:bg-[var(--sahha-primary-50)]"
            >
              {t("loginCta")}
            </button>
          </div>
        </div>
      )}

      {/* EMAIL */}
      {step === "email" && (
        <div className={cardCommon}>
          <BackBtn onClick={back} label={t("backStep")} />
          <h3 className="mt-3 text-[18px] font-bold text-[var(--sahha-text-900)]">{t("emailTitle")}</h3>
          <form onSubmit={submitEmail} className="mt-4 space-y-4">
            <input
              type="text"
              required
              autoFocus
              autoComplete="username"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              placeholder={t("emailPlaceholder")}
              className="h-12 w-full rounded-xl border border-[var(--sahha-border-default)] bg-white px-4 text-[14px] focus:border-[var(--sahha-primary-500)] focus:outline-none focus:ring-2 focus:ring-[var(--sahha-primary-200)]"
            />
            {error && <Err msg={error} />}
            <button
              type="submit"
              disabled={!identifier.trim()}
              className="inline-flex h-12 w-full items-center justify-center rounded-full bg-[var(--sahha-primary-600)] text-[14px] font-bold text-white disabled:opacity-60"
            >
              {t("continueCta")}
            </button>
          </form>
        </div>
      )}

      {/* PASSWORD */}
      {step === "password" && (
        <div className={cardCommon}>
          <BackBtn onClick={back} label={t("backStep")} />
          <h3 className="mt-3 text-[18px] font-bold text-[var(--sahha-text-900)]">{t("passwordTitle")}</h3>
          <p className="mt-1 text-[13px] text-[var(--sahha-text-600)]">{identifier}</p>
          <form onSubmit={submitPassword} className="mt-4 space-y-4">
            <div className="relative">
              <input
                type={showPwd ? "text" : "password"}
                required
                autoFocus
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t("passwordPlaceholder")}
                className="h-12 w-full rounded-xl border border-[var(--sahha-border-default)] bg-white px-4 pr-11 text-[14px] focus:border-[var(--sahha-primary-500)] focus:outline-none focus:ring-2 focus:ring-[var(--sahha-primary-200)]"
              />
              <button type="button" onClick={() => setShowPwd((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--sahha-text-500)]" aria-label={t("togglePasswordAria")}>
                {showPwd ? <EyeSlash size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <div className="flex items-center justify-between text-[13px]">
              <label className="inline-flex items-center gap-2 text-[var(--sahha-text-700)]">
                <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="h-4 w-4 rounded accent-[var(--sahha-primary-600)]" />
                {t("rememberMe")}
              </label>
              <Link href="/mot-de-passe-oublie" className="font-semibold text-[var(--sahha-primary-700)] hover:underline">
                {t("forgotPassword")}
              </Link>
            </div>
            {error && <Err msg={error} />}
            <button
              type="submit"
              disabled={loading || !password}
              className="inline-flex h-12 w-full items-center justify-center rounded-full bg-[var(--sahha-primary-600)] text-[14px] font-bold text-white disabled:opacity-60"
            >
              {loading ? t("loginSubmitting") : t("loginCta")}
            </button>
          </form>
        </div>
      )}

      {/* TOTP */}
      {step === "totp" && (
        <div className={cardCommon}>
          <BackBtn onClick={back} label={t("backStep")} />
          <h3 className="mt-3 text-[18px] font-bold text-[var(--sahha-text-900)]">{t("totpTitle")}</h3>
          <p className="mt-1 text-[13px] text-[var(--sahha-text-600)]">{t("totpHelp")}</p>
          <form onSubmit={submitTotp} className="mt-4 space-y-4">
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              autoFocus
              autoComplete="one-time-code"
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))}
              placeholder="000000"
              className="h-12 w-full rounded-xl border border-[var(--sahha-border-default)] bg-white px-4 text-center font-mono text-[18px] tracking-[0.5em] focus:border-[var(--sahha-primary-500)] focus:outline-none focus:ring-2 focus:ring-[var(--sahha-primary-200)]"
            />
            {error && <Err msg={error} />}
            <button
              type="submit"
              disabled={loading || totpCode.length !== 6}
              className="inline-flex h-12 w-full items-center justify-center rounded-full bg-[var(--sahha-primary-600)] text-[14px] font-bold text-white disabled:opacity-60"
            >
              {loading ? t("totpSubmitting") : t("totpSubmit")}
            </button>
          </form>
        </div>
      )}

      {/* SIGNUP */}
      {step === "signup" && (
        <div className={cardCommon}>
          <BackBtn onClick={back} label={t("backStep")} />
          <h3 className="mt-3 text-[18px] font-bold text-[var(--sahha-text-900)]">{t("signupTitle")}</h3>
          <form onSubmit={submitSignup} className="mt-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Input label={t("firstName")} value={firstName} onChange={setFirstName} required />
              <Input label={t("lastName")} value={lastName} onChange={setLastName} required />
            </div>
            <Input label={t("email")} type="email" value={identifier} onChange={setIdentifier} required autoComplete="email" />
            <Input label={t("phone")} type="tel" value={phone} onChange={setPhone} required placeholder={t("phonePlaceholder")} autoComplete="tel" />
            <div>
              <label className="block text-[12px] font-semibold text-[var(--sahha-text-700)]">{t("passwordLabel")}</label>
              <div className="relative mt-1">
                <input
                  type={showPwd ? "text" : "password"}
                  required
                  minLength={8}
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-11 w-full rounded-xl border border-[var(--sahha-border-default)] bg-white px-3 pr-10 text-[13.5px] focus:border-[var(--sahha-primary-500)] focus:outline-none focus:ring-2 focus:ring-[var(--sahha-primary-200)]"
                />
                <button type="button" onClick={() => setShowPwd((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--sahha-text-500)]" aria-label={t("togglePasswordAria")}>
                  {showPwd ? <EyeSlash size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <Input label={t("passwordConfirm")} type={showPwd ? "text" : "password"} value={confirm} onChange={setConfirm} required autoComplete="new-password" />
            <label className="flex items-start gap-2 pt-2 text-[12px] text-[var(--sahha-text-600)]">
              <input type="checkbox" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} className="mt-0.5 h-4 w-4 shrink-0 rounded accent-[var(--sahha-primary-600)]" />
              <span>
                {t.rich("acceptTermsHTML", {
                  link1: (chunks) => (<Link href="/cgu" className="font-semibold underline">{chunks}</Link>),
                  link2: (chunks) => (<Link href="/politique-confidentialite" className="font-semibold underline">{chunks}</Link>),
                })}
              </span>
            </label>
            {error && <Err msg={error} />}
            <button
              type="submit"
              disabled={loading}
              className="inline-flex h-12 w-full items-center justify-center rounded-full bg-[var(--sahha-primary-600)] text-[14px] font-bold text-white disabled:opacity-60"
            >
              {loading ? t("signupSubmitting") : t("signupSubmit")}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

function Bullet({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <li className="flex items-center gap-3 text-[13.5px] text-[var(--sahha-text-700)]">
      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[var(--sahha-primary-50)] text-[var(--sahha-primary-700)]">{icon}</span>
      <span>{label}</span>
    </li>
  );
}
function BackBtn({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 rounded-full bg-[var(--sahha-surface-1)] px-3 py-1.5 text-[12.5px] font-bold text-[var(--sahha-text-700)] hover:bg-[var(--sahha-border-subtle)]"
    >
      <ArrowLeft size={12} weight="bold" /> {label}
    </button>
  );
}
function Err({ msg }: { msg: string }) {
  return <p className="rounded-xl bg-red-50 px-3 py-2 text-[12.5px] text-red-700">⚠ {msg}</p>;
}
function Input({ label, value, onChange, type = "text", required = false, placeholder, autoComplete }: { label: string; value: string; onChange: (v: string) => void; type?: string; required?: boolean; placeholder?: string; autoComplete?: string }) {
  return (
    <div>
      <label className="block text-[12px] font-semibold text-[var(--sahha-text-700)]">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
        autoComplete={autoComplete}
        className="mt-1 h-11 w-full rounded-xl border border-[var(--sahha-border-default)] bg-white px-3 text-[13.5px] focus:border-[var(--sahha-primary-500)] focus:outline-none focus:ring-2 focus:ring-[var(--sahha-primary-200)]"
      />
    </div>
  );
}
